// src/components/Projects.tsx
import React from "react";
import { Link } from "react-router-dom";
import { projects } from "../data";
import type { Project } from "../types";
import { AnimatedWavePlaceholder } from "./AnimatedWavePlaceholder";

const STATUS_ORDER: Record<
  Exclude<Project["status"], undefined>,
  number
> = {
  Complete: 1,
  "In Progress": 0,
  "Coming Soon": 2,
};

const STATUS_INFO: Record<
  Exclude<Project["status"], undefined>,
  { label: string; icon: string; classes: string }
> = {
  Complete: {
    label: "Complete",
    icon: "✅",
    classes: "bg-green-600 text-white",
  },
  "In Progress": {
    label: "In Progress",
    icon: "🔨",
    classes: "bg-blue-600 text-white",
  },
  "Coming Soon": {
    label: "Coming Soon",
    icon: "⏳",
    classes: "bg-yellow-500 text-black",
  },
};

const Projects: React.FC = () => {
  const handleExternalClick = (url: string) => {
    window.location.href = url;
  };

  const sortedProjects = [...projects].sort((a, b) => {
    const aOrder = a.status ? STATUS_ORDER[a.status] : 99;
    const bOrder = b.status ? STATUS_ORDER[b.status] : 99;
    return aOrder - bOrder;
  });

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold text-white mb-6">Projects</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sortedProjects.map((project) => {
          const status = project.status
            ? STATUS_INFO[project.status]
            : null;

          const cardContent = (
            <div className="relative w-full h-48 mb-4 rounded overflow-hidden group">
              {project.status === "Coming Soon" ? (
                <AnimatedWavePlaceholder className="w-full h-full" />
              ) : (
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-opacity duration-300 md:group-hover:opacity-0"
                />
              )}

              <div
                aria-hidden="true"
                className="
                  absolute inset-0
                  hidden md:flex
                  items-center justify-center
                  p-4
                  text-gray-300 text-center
                  bg-black/80
                  opacity-0
                  transition-opacity duration-300
                  group-hover:opacity-100
                  group-focus-within:opacity-100
                "
              >
                {project.description}
              </div>

              {status && (
                <span
                  className={`
                    absolute top-2 left-2
                    px-2 py-1
                    text-xs font-semibold
                    rounded
                    transition-opacity duration-300
                    md:group-hover:opacity-0
                    group-focus-within:opacity-0
                    ${status.classes}
                  `}
                >
                  {status.icon} {status.label}
                </span>
              )}
            </div>
          );

          const cardBody = (
            <>
              {cardContent}

              <h2 className="text-xl font-bold text-white">
                {project.title}
              </h2>
              <p className="text-gray-300">{project.subtitle}</p>

              <div className="mt-3 pt-3 border-t border-white/10 md:hidden">
                {project.status === "Coming Soon" && (
                  <p className="text-sm text-gray-400 italic mb-1">
                    In development
                  </p>
                )}
                <p className="text-sm text-gray-300" id={`project-desc-${project.id}`}>
                  {project.description}
                </p>
              </div>
            </>
          );

          return project.external ? (
            <div
              key={project.id}
              onClick={() => handleExternalClick(project.link)}
              aria-describedby={`project-desc-${project.id}`}
              className="cursor-pointer block p-4 bg-gray-800 rounded hover:bg-gray-700 transition project-card retro-bulletin-wrapper"
            >
              {cardBody}
            </div>
          ) : (
            <Link
              key={project.id}
              to={project.link}
              aria-describedby={`project-desc-${project.id}`}
              className="block p-4 bg-gray-800 rounded hover:bg-gray-700 transition project-card retro-bulletin-wrapper"
            >
              {cardBody}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Projects;
